import { pluralize } from "~/utils/formatting"
import type { DraftChange } from "../../DraftCommandbar"
import { packageWindowLabel, railWindowLabel } from "../../packageDisplay"
import type { PackageFolder, RangePackage } from "../../types"

export type PackageTree = { packages: RangePackage[]; folders: PackageFolder[] }

/** Packages and folders marked for deletion, deleted (soft) when the draft is published. */
export type PackageDeletions = { packageIds: string[]; folderIds: string[] }

export type PackageDraftChanges = {
  changes: DraftChange[]
  /** Created or changed by the draft, so not published yet. */
  packageIds: ReadonlySet<string>
  folderIds: ReadonlySet<string>
}

const sameSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false
  const set = new Set(a)
  return b.every((id) => set.has(id))
}

function movesText(before: string[], after: string[]) {
  const was = new Set(before)
  const now = new Set(after)
  const added = after.filter((id) => !was.has(id)).length
  const removed = before.filter((id) => !now.has(id)).length
  return [added > 0 && `+${added}`, removed > 0 && `−${removed}`].filter(Boolean).join(", ")
}

const storeListText = (before: string[], after: string[]) =>
  `${pluralize(after.length, "store")} (${movesText(before, after)})`

/** Rows for every sibling group whose order changed among the items both trees hold. */
function orderChanges<T extends { id: string }>(
  kind: "package" | "folder",
  before: T[],
  after: T[],
  parentOf: (item: T) => string | null,
  parentName: (id: string | null) => string,
): DraftChange[] {
  const afterIds = new Set(after.map((item) => item.id))
  const beforeIds = new Set(before.map((item) => item.id))
  const groups = (items: T[], keep: Set<string>) =>
    Map.groupBy(
      items.filter((item) => keep.has(item.id)),
      parentOf,
    )
  const was = groups(before, afterIds)
  const now = groups(after, beforeIds)
  return [...now].flatMap(([parentId, items]) => {
    const old = was.get(parentId) ?? []
    const moved =
      old.length === items.length && items.some((item, index) => old[index]?.id !== item.id)
    if (!moved) return []
    return [
      {
        key: `order:${kind}:${parentId ?? "top"}`,
        label: `${kind === "package" ? "Package" : "Folder"} order · ${parentName(parentId)}`,
        before: "Published order",
        after: "New order",
      },
    ]
  })
}

/**
 * What the draft changes, from the published tree and the preview: one row
 * per field a planner set, not per package that inherits it from a folder.
 * Deletions are listed apart, as they apply only when published.
 */
export function packageDraftChanges(
  published: PackageTree,
  preview: PackageTree,
  deletions: PackageDeletions,
): PackageDraftChanges {
  const changes: DraftChange[] = []
  const packageIds = new Set<string>()
  const folderIds = new Set<string>()
  const folderName = new Map(
    [...published.folders, ...preview.folders].map((folder) => [folder.id, folder.name]),
  )
  const nameOf = (folderId: string | null, none: string) =>
    folderId === null ? none : (folderName.get(folderId) ?? none)

  const savedFolders = new Map(published.folders.map((folder) => [folder.id, folder]))
  for (const folder of preview.folders) {
    const label = `Folder · ${folder.name}`
    const row = (field: string, before: string, after: string) => {
      folderIds.add(folder.id)
      changes.push({
        key: `folder:${folder.id}:${field}`,
        label: `${label} · ${field}`,
        before,
        after,
      })
    }
    const saved = savedFolders.get(folder.id)
    if (!saved) {
      folderIds.add(folder.id)
      changes.push({ key: `folder:${folder.id}`, label, before: "—", after: "Created" })
      continue
    }
    if (saved.name !== folder.name) row("Name", saved.name, folder.name)
    if (saved.parentId !== folder.parentId) {
      row("Place", nameOf(saved.parentId, "Top level"), nameOf(folder.parentId, "Top level"))
    }
    if (
      saved.ownStartDate !== folder.ownStartDate ||
      saved.ownCutoffDate !== folder.ownCutoffDate
    ) {
      row("Dates", folderDatesText(saved), folderDatesText(folder))
    }
    if (saved.ownStores !== folder.ownStores) {
      row(
        "Stores",
        folder.ownStores ? "Follows parent" : "Own set",
        folder.ownStores ? "Own set" : "Follows parent",
      )
    } else if (folder.ownStores && !sameSet(saved.storeIds, folder.storeIds)) {
      row(
        "Stores",
        pluralize(saved.storeIds.length, "store"),
        storeListText(saved.storeIds, folder.storeIds),
      )
    }
    if (saved.ownDeactivated !== folder.ownDeactivated) {
      row("Status", activeText(saved.ownDeactivated), activeText(folder.ownDeactivated))
    }
  }
  const previewFolderIds = new Set(preview.folders.map((folder) => folder.id))
  for (const saved of published.folders) {
    if (previewFolderIds.has(saved.id)) continue
    changes.push({
      key: `folder:${saved.id}`,
      label: `Folder · ${saved.name}`,
      before: "Subfolder",
      after: "Removed",
    })
  }

  const savedPackages = new Map(published.packages.map((pkg) => [pkg.id, pkg]))
  for (const pkg of preview.packages) {
    const row = (field: string, before: string, after: string) => {
      packageIds.add(pkg.id)
      changes.push({
        key: `package:${pkg.id}:${field}`,
        label: `${pkg.name} · ${field}`,
        before,
        after,
      })
    }
    const saved = savedPackages.get(pkg.id)
    if (!saved) {
      packageIds.add(pkg.id)
      changes.push({ key: `package:${pkg.id}`, label: pkg.name, before: "—", after: "Created" })
      continue
    }
    if (saved.name !== pkg.name) row("Name", saved.name, pkg.name)
    if (saved.folderId !== pkg.folderId) {
      row("Folder", nameOf(saved.folderId, "No folder"), nameOf(pkg.folderId, "No folder"))
    }
    const ownWindowChanged =
      pkg.datesFolderId === null &&
      (saved.startDate !== pkg.startDate || saved.cutoffDate !== pkg.cutoffDate)
    if (saved.datesFolderId !== pkg.datesFolderId || ownWindowChanged) {
      row("Dates", datesText(saved, nameOf), datesText(pkg, nameOf))
    }
    if (saved.storesFolderId !== pkg.storesFolderId) {
      row("Stores", storesSourceText(saved, nameOf), storesSourceText(pkg, nameOf))
    } else if (pkg.storesFolderId === null && !sameSet(saved.storeIds, pkg.storeIds)) {
      row(
        "Stores",
        pluralize(saved.storeIds.length, "store"),
        storeListText(saved.storeIds, pkg.storeIds),
      )
    }
    if (saved.ownDeactivated !== pkg.ownDeactivated) {
      row("Status", activeText(saved.ownDeactivated), activeText(pkg.ownDeactivated))
    }
    if (!sameSet(saved.skuIds ?? [], pkg.skuIds ?? [])) {
      row(
        "Variants",
        rangeText(saved),
        `${rangeText(pkg)} (SKUs ${movesText(saved.skuIds ?? [], pkg.skuIds ?? [])})`,
      )
    }
  }

  changes.push(
    ...orderChanges(
      "folder",
      published.folders,
      preview.folders,
      (f) => f.parentId,
      (id) => nameOf(id, "Top level"),
    ),
    ...orderChanges(
      "package",
      published.packages,
      preview.packages,
      (p) => p.folderId,
      (id) => nameOf(id, "No folder"),
    ),
  )

  const deletedPackageName = new Map(
    [...published.packages, ...preview.packages].map((pkg) => [pkg.id, pkg.name]),
  )
  changes.push(
    ...deletions.folderIds.map((id) => ({
      key: `delete:folder:${id}`,
      label: `Folder · ${folderName.get(id) ?? id}`,
      before: "Kept",
      after: "Deleted",
    })),
    ...deletions.packageIds.map((id) => ({
      key: `delete:package:${id}`,
      label: deletedPackageName.get(id) ?? id,
      before: "Kept",
      after: "Deleted",
    })),
  )
  return { changes, packageIds, folderIds }
}

const activeText = (deactivated: boolean) => (deactivated ? "Archived" : "Active")

const rangeText = (pkg: RangePackage) =>
  `${pluralize(pkg.productIds.length, "variant")}, ${pluralize(pkg.skuIds?.length ?? 0, "SKU")}`

const folderDatesText = (folder: PackageFolder) =>
  packageWindowLabel(folder.ownStartDate, folder.ownCutoffDate)

function datesText(pkg: RangePackage, nameOf: (id: string | null, none: string) => string) {
  const window = railWindowLabel(pkg)
  return pkg.datesFolderId === null
    ? window
    : `${window}, from ${nameOf(pkg.datesFolderId, "folder")}`
}

function storesSourceText(pkg: RangePackage, nameOf: (id: string | null, none: string) => string) {
  const count = pluralize(pkg.storeIds.length, "store")
  return pkg.storesFolderId === null
    ? `Own set, ${count}`
    : `${count}, from ${nameOf(pkg.storesFolderId, "folder")}`
}
